import type { ContractActionsResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodContractActionsResponseSchema } from './validations';

/**
 * Fetches available actions for a contract based on its current status.
 * Use this to dynamically render action buttons in the UI.
 *
 * @param contractId - The UUID of the contract to fetch actions for.
 * @param options - Optional request options for the ky client.
 * @returns The available actions and their metadata.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_actions_contracts__contract_id__actions_get
 */
export const fetchContractActions = async (
  contractId: UUID,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CONTRACT.ACTIONS(contractId), {
      ...options,
    })
    .json<ContractActionsResponse | undefined>();

  // Validate the response with Zod
  const validatedResponse =
    zodContractActionsResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      error: {
        errorMessage: validatedResponse?.error?.message,
        errors: validatedResponse?.error?.issues,
        response,
      },
      message: 'Contract actions API response has validation errors',
    });
  }

  return response;
};
